import { useState, useEffect, useCallback } from "react"
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  Button,
  Form,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"

import ButtonTooltip from "components/Common/ButtonTooltip"
import { useToastMessage } from "hooks/useToastMessage"
import { updateVehicleImagesCache, useUploadImages } from "hooks/imageHook"
import { useEditVehicleStock } from "hooks/stockHook"
import { useQueryClient } from "@tanstack/react-query"
import { useImageViewer } from "hooks/useImageViewer"
import ImageViewer from "components/Common/ImageViewer/ImageViewer"

// Professional file upload card component
const FileUploadCard = ({
  label,
  value,
  type,
  originalFile,
  onFileChange,
  onDelete,
  onRestore,
  openImageViewer,
}) => {
  const hasExistingFile = originalFile?.id
  const hasNewFile = value instanceof File
  const isDeleted = !value && hasExistingFile
  const isPdf =
    value?.url?.toLowerCase()?.endsWith(".pdf") ||
    (value instanceof File && value.type === "application/pdf")
  const fileSize = value?.size || (value instanceof File ? value.size : 0)

  const formatFileSize = bytes => {
    if (!bytes) return ""
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
    return (bytes / (1024 * 1024)).toFixed(1) + " MB"
  }

  const getFileName = () => {
    if (value instanceof File) return value.name
    if (value?.url) {
      const urlParts = value.url.split("/")
      return urlParts[urlParts.length - 1] || "File"
    }
    return ""
  }

  const fileName = getFileName()
  const truncatedFileName =
    fileName.length > 30 ? fileName.substring(0, 27) + "..." : fileName

  return (
    <Col xs={12} className="mb-4">
      <div className="border rounded-3 bg-white p-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <div className="bg-light rounded-2 p-2 me-3">
              <i
                className={`bx bxs-file-${
                  isPdf ? "pdf" : "image"
                } text-primary fs-4`}
              ></i>
            </div>
            <div>
              <Label className="fw-semibold mb-1">{label}</Label>
              {hasExistingFile && !isDeleted && !hasNewFile && (
                <div className="text-muted small">
                  <i className="bx bx-check-circle text-success me-1"></i>
                  Current file uploaded
                </div>
              )}
            </div>
          </div>

          {/* Status Badges */}
          {hasNewFile && (
            <span className="badge bg-warning bg-opacity-10 text-warning px-3 py-2 rounded-pill">
              <i className="bx bx-sync me-1"></i>
              Pending replacement
            </span>
          )}
          {isDeleted && (
            <span className="badge bg-danger bg-opacity-10 text-danger px-3 py-2 rounded-pill">
              <i className="bx bx-trash me-1"></i>
              Pending deletion
            </span>
          )}
        </div>

        {/* Content */}
        {value ? (
          <div className="bg-light bg-opacity-50 rounded-3 p-4">
            {/* Preview */}
            <div className="d-flex align-items-center gap-4">
              {/* Thumbnail/Icon */}
              <div className="flex-shrink-0">
                {isPdf ? (
                  <div className="bg-white rounded-2 p-3 shadow-sm">
                    <i className="bx bxs-file-pdf fs-1 text-danger"></i>
                  </div>
                ) : (
                  <div
                    className="bg-white rounded-2 p-1 shadow-sm"
                    style={{
                      width: "80px",
                      height: "80px",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={
                        value.url ||
                        (value instanceof File && URL.createObjectURL(value))
                      }
                      alt="preview"
                      className="rounded-2"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                )}
              </div>

              {/* File Info */}
              <div className="flex-grow-1 min-width-0">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bx bx-file text-muted"></i>
                  <span
                    className="text-dark fw-medium text-truncate"
                    title={fileName}
                  >
                    {truncatedFileName}
                  </span>
                </div>
                <div className="d-flex gap-3 text-muted small">
                  <span>
                    <i className="bx bx-hdd me-1"></i>
                    {formatFileSize(fileSize)}
                  </span>
                  {value.url && (
                    <span>
                      <i className="bx bx-check-shield me-1"></i>
                      Uploaded
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="d-flex gap-2 ms-auto">
                {value.url && (
                  <Button
                    color="light"
                    size="sm"
                    className="rounded-circle p-2"
                    style={{ width: "36px", height: "36px" }}
                    onClick={() => openImageViewer(value.url, label)}
                  >
                    <i className="bx bx-show"></i>
                  </Button>
                )}
                <div className="d-flex gap-2">
                  <Input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={e => onFileChange(e, type)}
                    className="d-none"
                    id={`file-${type}`}
                  />
                  <Button
                    color="light"
                    size="sm"
                    className="rounded-circle p-2"
                    style={{ width: "36px", height: "36px" }}
                    onClick={() =>
                      document.getElementById(`file-${type}`).click()
                    }
                  >
                    <i className="bx bx-upload"></i>
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    className="rounded-circle p-2"
                    style={{ width: "36px", height: "36px" }}
                    onClick={() => onDelete(type)}
                  >
                    <i className="bx bx-trash"></i>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : isDeleted ? (
          <div className="bg-danger bg-opacity-10 rounded-3 p-4">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <div className="bg-white rounded-2 p-2">
                  <i className="bx bx-error-circle text-danger fs-4"></i>
                </div>
                <div>
                  <span className="text-danger fw-medium">
                    File marked for deletion
                  </span>
                  <p className="text-muted small mb-0">
                    This file will be removed when you save changes
                  </p>
                </div>
              </div>
              <Button
                color="light"
                size="sm"
                className="px-4 py-2 rounded-3"
                onClick={() => onRestore(type)}
              >
                <i className="bx bx-undo me-2"></i>
                Undo
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="bg-light rounded-3 p-4 text-center"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById(`file-${type}`).click()}
          >
            <Input
              type="file"
              accept="image/*,application/pdf"
              onChange={e => onFileChange(e, type)}
              className="d-none"
              id={`file-${type}`}
            />
            <div className="py-3">
              <div className="bg-white rounded-circle p-3 d-inline-block mb-3">
                <i className="bx bx-cloud-upload fs-2 text-primary"></i>
              </div>
              <p className="fw-medium mb-1">Click to upload {label}</p>
              <p className="text-muted small mb-0">
                PDF or Image files, max 10MB
              </p>
            </div>
          </div>
        )}
      </div>
    </Col>
  )
}

export default function StockAdditionalInfoUpdate({ stock = null }) {
  const [modal, setModal] = useState(false)

  // File states
  const [auctionSheet, setAuctionSheet] = useState(null)
  const [trueReport, setTrueReport] = useState(null)
  const [documents, setDocuments] = useState(null)

  // Store original files
  const [originalFiles, setOriginalFiles] = useState({
    auction_sheet: null,
    true_report: null,
    documents: null,
  })

  const {
    viewerOpen,
    selectedImageUrl,
    imageTitle,
    openImageViewer,
    closeImageViewer,
  } = useImageViewer()

  const { handleResponse } = useToastMessage()
  const uploadImagesMutation = useUploadImages()
  const { mutateAsync: editStock, isPending: stockEditLoading } =
    useEditVehicleStock()
  const queryClient = useQueryClient()

  const resetState = () => {
    setAuctionSheet(null)
    setTrueReport(null)
    setDocuments(null)
    setOriginalFiles({
      auction_sheet: null,
      true_report: null,
      documents: null,
    })
  }

  const toggle = () => {
    if (modal) resetState()
    setModal(!modal)
  }

  useEffect(() => {
    if (stock && modal) {
      setAuctionSheet(stock.auction_sheet || null)
      setTrueReport(stock.true_report || null)
      setDocuments(stock.documents || null)
      setOriginalFiles({
        auction_sheet: stock.auction_sheet || null,
        true_report: stock.true_report || null,
        documents: stock.documents || null,
      })
    }
  }, [stock, modal])

  const validationSchema = Yup.object({})

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {},
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: useCallback(async () => {
      try {
        const formData = new FormData()
        const filesToUpdate = {}

        const processFileType = (type, currentFile, originalFile) => {
          const isNewFile = currentFile instanceof File
          const isDeleted = !currentFile && originalFile
          const isReplaced = isNewFile && originalFile

          if (isReplaced || isDeleted) {
            formData.append("delete_file_ids[]", originalFile.id)
          }

          if (isNewFile) {
            formData.append("images[]", currentFile)
            filesToUpdate[type] = "new"
          } else if (isDeleted) {
            filesToUpdate[type] = null
          } else if (currentFile?.id) {
            filesToUpdate[type] = currentFile
          } else {
            filesToUpdate[type] = null
          }
        }

        processFileType(
          "auction_sheet",
          auctionSheet,
          originalFiles.auction_sheet
        )
        processFileType("true_report", trueReport, originalFiles.true_report)
        processFileType("documents", documents, originalFiles.documents)

        let uploadedFiles = {
          auction_sheet: null,
          true_report: null,
          documents: null,
        }

        if (formData.has("images[]") || formData.has("delete_file_ids[]")) {
          const res = await uploadImagesMutation.mutateAsync(formData)

          if (res.success) {
            const newFiles = [...res.data]
            Object.keys(filesToUpdate).forEach(key => {
              if (filesToUpdate[key] === "new") {
                const newFile = newFiles.shift()
                uploadedFiles[key] = newFile
                  ? {
                      id: newFile.id,
                      url: newFile.url,
                      size: newFile.size || 0,
                    }
                  : null
              } else {
                uploadedFiles[key] = filesToUpdate[key]
              }
            })
          }
        } else {
          uploadedFiles = {
            auction_sheet: auctionSheet?.id ? auctionSheet : null,
            true_report: trueReport?.id ? trueReport : null,
            documents: documents?.id ? documents : null,
          }
        }

        const stockRes = await editStock({
          id: stock?.id,
          auction_sheet: uploadedFiles.auction_sheet,
          true_report: uploadedFiles.true_report,
          documents: uploadedFiles.documents,
        })

        const updatedImageIds = stockRes?.data?.images || []
        const auctionId = stockRes?.data?.auction_sheet?.id || null

        updateVehicleImagesCache({
          vehicleId: stock?.id,
          images: updatedImageIds,
          auctionId,
          queryClient,
        })

        handleResponse(
          stockRes,
          "Additional information updated successfully.",
          true
        )
        if (stockRes.success) toggle()
      } catch (error) {
        console.error("Failed to update additional information:", error)
      }
    }, [
      auctionSheet,
      trueReport,
      documents,
      originalFiles,
      uploadImagesMutation,
      editStock,
      stock?.id,
      handleResponse,
      queryClient,
    ]),
  })

  const handleSingleFileChange = (e, type) => {
    const file = e.target.files[0] || null
    if (type === "auction_sheet") setAuctionSheet(file)
    if (type === "true_report") setTrueReport(file)
    if (type === "documents") setDocuments(file)
  }

  const handleDeleteFile = type => {
    if (type === "auction_sheet") setAuctionSheet(null)
    if (type === "true_report") setTrueReport(null)
    if (type === "documents") setDocuments(null)
  }

  const handleRestoreFile = type => {
    if (type === "auction_sheet") setAuctionSheet(originalFiles.auction_sheet)
    if (type === "true_report") setTrueReport(originalFiles.true_report)
    if (type === "documents") setDocuments(originalFiles.documents)
  }

  return (
    <>
      <ButtonTooltip
        type="edit"
        onClick={toggle}
        btnId="edit-additional-info"
        btnName="Edit Documents"
      />

      <Modal
        isOpen={modal}
        toggle={toggle}
        centered
        size="lg"
        className="document-upload-modal"
      >
        <ModalHeader toggle={toggle} className="border-0 pb-0 pt-4 px-4">
          <div>
            <h4 className="mb-1">Additional Documents</h4>
            <p className="text-muted small mb-0 fw-normal">
              Manage auction sheets, true reports, and other documents
            </p>
          </div>
        </ModalHeader>
        <ModalBody className="px-4 pb-4">
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <FileUploadCard
                label="Auction Sheet"
                value={auctionSheet}
                type="auction_sheet"
                originalFile={originalFiles.auction_sheet}
                onFileChange={handleSingleFileChange}
                onDelete={handleDeleteFile}
                onRestore={handleRestoreFile}
                openImageViewer={openImageViewer}
              />
              <FileUploadCard
                label="True Report"
                value={trueReport}
                type="true_report"
                originalFile={originalFiles.true_report}
                onFileChange={handleSingleFileChange}
                onDelete={handleDeleteFile}
                onRestore={handleRestoreFile}
                openImageViewer={openImageViewer}
              />
              <FileUploadCard
                label="Documents"
                value={documents}
                type="documents"
                originalFile={originalFiles.documents}
                onFileChange={handleSingleFileChange}
                onDelete={handleDeleteFile}
                onRestore={handleRestoreFile}
                openImageViewer={openImageViewer}
              />
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-5 pt-2 border-top">
              <Button
                type="button"
                color="light"
                onClick={toggle}
                className="px-4 py-2 rounded-3"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={stockEditLoading || uploadImagesMutation.isPending}
                className="px-5 py-2 rounded-3"
              >
                {stockEditLoading || uploadImagesMutation.isPending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bx bx-save me-2"></i>
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      <style>{`
        :global(.document-upload-modal .modal-content) {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
        }
        
        :global(.document-upload-modal .modal-header) {
          border-bottom: none;
        }
        
        :global(.document-upload-modal .btn-close) {
          background-color: #f8f9fa;
          border-radius: 50%;
          padding: 12px;
          opacity: 0.8;
          transition: all 0.2s;
        }
        
        :global(.document-upload-modal .btn-close:hover) {
          background-color: #e9ecef;
          opacity: 1;
        }
      `}</style>

      <ImageViewer
        images={[selectedImageUrl]}
        initialIndex={0}
        isOpen={viewerOpen}
        onClose={closeImageViewer}
        title={imageTitle}
      />
    </>
  )
}
